// ─────────────────────────────────────────────────────────────────────────
// dateRules — the ONE implementation of every date-comparison rule, shared by
// AddFormV1 and EditFormV1.
//
// WHY THIS MODULE EXISTS
// The rule engine was duplicated verbatim in both forms. That is how a fix
// lands on Add and silently misses Edit — exactly what had already happened to
// `minLength3` (defined only in AddFormV1, a no-op on the edit form). A
// requirement phrased as "candidate AND submission, add AND edit must behave
// the same" cannot be satisfied by two copies that merely look alike, so the
// logic lives here and both forms import it.
//
// WHAT A RULE LOOKS LIKE (all options optional, absent === previous behaviour)
//
//   { type:    'dateAfterField',            // this field must be AFTER…
//     value:   'startDate',                 // …this sibling
//     strict:  true,                        // equal dates are INVALID
//     minGap:  { value: 6, unit: 'month' }, // and at least 6 months apart
//     minGapFrom: 'duration',               // …or read the gap from a sibling
//     maxGap:  { value: 40, unit: 'year' },
//     message: 'End Date must be after Start Date' }
//
// `dateBeforeField` is the mirror image. Writing BOTH (start declares
// dateBeforeField(end), end declares dateAfterField(start)) is what makes the
// constraint show up in BOTH pickers: pick 7 July as Start and the End picker
// greys out everything up to and including 7 July, and vice-versa. The two
// rules are generated from one table in the seeder so they cannot drift.
//
// Every rule drives BOTH the submit-time validator and the picker's
// `disabledDate` from the same object — a greyed-out calendar that disagrees
// with the error message is worse than either alone.
// ─────────────────────────────────────────────────────────────────────────
import dayjs from 'dayjs';
import { appNow } from '../../services/timezone';

export const isEmptyValue = (v) => v === undefined || v === null || v === '';

/** Milliseconds for an instant-comparable value (dayjs, Date, ISO string). */
export function getComparableDateTime(value) {
  if (!value) return null;
  if (typeof value.valueOf === 'function') {
    const time = value.valueOf();
    return Number.isNaN(time) ? null : time;
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/** Milliseconds at the START of the value's day — the unit date rules compare in. */
export function getComparableDateDay(value) {
  if (!value) return null;
  const date = dayjs(value);
  return date.isValid() ? date.startOf('day').valueOf() : null;
}

/** Minutes since midnight. TimePicker values share a day, so startOf('day') would make every time equal. */
export function getComparableTimeOfDay(value) {
  if (!value) return null;
  const time = dayjs(value);
  return time.isValid() ? time.hour() * 60 + time.minute() : null;
}

// Units dayjs accepts for add/subtract, mapped from what an admin might type.
const GAP_UNITS = {
  d: 'day', day: 'day', days: 'day',
  w: 'week', week: 'week', weeks: 'week',
  m: 'month', mo: 'month', month: 'month', months: 'month',
  y: 'year', yr: 'year', year: 'year', years: 'year',
};

/**
 * parseGap — normalise every shape a gap can arrive in into {value, unit}.
 *
 * It has to be forgiving because one of the sources is a DROPDOWN whose options
 * are admin-authored master data: "6 months", "6m", "6", {value:6,unit:'month'}
 * all have to mean the same thing, or the config screen becomes a trap where
 * the wrong-but-reasonable spelling silently disables the rule.
 *
 * A bare number means months — the unit the duration dropdown is expressed in.
 * Returns null for anything that carries no usable magnitude.
 */
export function parseGap(raw) {
  if (raw === null || raw === undefined || raw === '') return null;

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const value = Number(raw.value ?? raw.count ?? raw.amount);
    if (!Number.isFinite(value) || value <= 0) return null;
    return { value, unit: GAP_UNITS[String(raw.unit ?? 'month').toLowerCase()] ?? 'month' };
  }

  const text = String(raw).trim().toLowerCase();
  if (!text) return null;
  const match = text.match(/^(\d+(?:\.\d+)?)\s*([a-z]*)$/);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  return { value, unit: GAP_UNITS[match[2]] ?? 'month' };
}

/**
 * resolveGap — the gap actually in force for this field right now.
 *
 * `minGapFrom` names a sibling whose CURRENT value supplies the gap, which is
 * how the "select 6 months and the pickers tighten to 6 months" dropdown works
 * without any rule rewriting. It wins over a static `minGap` when it holds a
 * usable value, and falls back to the static one when the dropdown is empty.
 */
export function resolveGap(rule, readSibling) {
  const fromField = rule?.minGapFrom ?? rule?.gapFrom;
  if (fromField && typeof readSibling === 'function') {
    const dynamic = parseGap(readSibling(fromField));
    if (dynamic) return dynamic;
  }
  return parseGap(rule?.minGap);
}

/**
 * compareBoundary — the earliest (dateAfterField) or latest (dateBeforeField)
 * day this field may hold, given the other end of the pair.
 *
 * Returns a day-start timestamp, or null when there is nothing to constrain.
 */
export function compareBoundary(type, compareDay, rule, readSibling) {
  if (compareDay === null) return null;
  const gap = resolveGap(rule, readSibling);
  let boundary = dayjs(compareDay);
  if (gap) {
    boundary = type === 'dateAfterField'
      ? boundary.add(gap.value, gap.unit)
      : boundary.subtract(gap.value, gap.unit);
  }
  return boundary.startOf('day').valueOf();
}

/**
 * violatesPair — is `inputDay` on the wrong side of the boundary?
 *
 * `strict` is what makes "the end date can't be the same day as the start date"
 * work: without it the rule reads "on or after", which is the behaviour that
 * let 7 July → 7 July through.
 */
export function violatesPair(type, inputDay, compareDay, rule, readSibling) {
  if (inputDay === null || compareDay === null) return false;
  const boundary = compareBoundary(type, compareDay, rule, readSibling);
  if (boundary === null) return false;
  const strict = Boolean(rule?.strict) && !resolveGap(rule, readSibling);
  if (type === 'dateAfterField') {
    return strict ? inputDay <= boundary : inputDay < boundary;
  }
  return strict ? inputDay >= boundary : inputDay > boundary;
}

/**
 * pairMessage — the error text, falling back to something that names the real
 * constraint rather than a generic "invalid date".
 */
export function pairMessage(type, label, rule, readSibling) {
  if (rule?.message) return rule.message;
  const other = rule?.value ?? rule?.compareField ?? rule?.field ?? 'the paired date';
  const gap = resolveGap(rule, readSibling);
  if (gap) {
    const unit = gap.value === 1 ? gap.unit : `${gap.unit}s`;
    return type === 'dateAfterField'
      ? `${label} must be at least ${gap.value} ${unit} after ${other}`
      : `${label} must be at least ${gap.value} ${unit} before ${other}`;
  }
  if (rule?.strict) {
    return type === 'dateAfterField'
      ? `${label} must be after ${other}`
      : `${label} must be before ${other}`;
  }
  return type === 'dateAfterField'
    ? `${label} must be on or after ${other}`
    : `${label} must be on or before ${other}`;
}

/**
 * buildPairValidator — the antd rule object for dateAfterField/dateBeforeField.
 *
 * `readSibling(fieldName)` resolves a sibling's CURRENT value with the caller's
 * own row-vs-top-level scoping, so this module never needs to know it is inside
 * a repeatable group.
 */
export function buildPairValidator({ type, label, rule, readSibling }) {
  return {
    validator: async (_, input) => {
      const compareField = rule?.value ?? rule?.compareField ?? rule?.field;
      // An empty value on EITHER side is the `required` rule's business —
      // otherwise an optional date pair becomes mandatory the moment one half
      // is filled in.
      if (!compareField || isEmptyValue(input)) return Promise.resolve();
      const compareValue = readSibling(compareField);
      if (isEmptyValue(compareValue)) return Promise.resolve();

      const inputDay = getComparableDateDay(input);
      const compareDay = getComparableDateDay(compareValue);
      if (inputDay === null || compareDay === null) return Promise.resolve();

      return violatesPair(type, inputDay, compareDay, rule, readSibling)
        ? Promise.reject(new Error(pairMessage(type, label, rule, readSibling)))
        : Promise.resolve();
    },
  };
}

/**
 * buildDisabledDate — the DatePicker `disabledDate` predicate for a field,
 * assembled from every date rule it declares.
 *
 * `now` is injected so the "today" boundary can come from the application's
 * configured timezone rather than the browser's (see services/timezone.js) —
 * a user in another zone would otherwise have noFutureDate reject their own
 * today, or accept a tomorrow.
 */
/**
 * violatesRowHierarchy — does this date clash with an ADJACENT ROW?
 *
 * A newest-first list (education, work experience) says something the per-row
 * rules cannot: row 2 happened BEFORE row 1. So once row 1 has a start date,
 * every date at or after it is impossible for row 2 — you cannot have finished
 * a later qualification before starting an earlier one.
 *
 * Constraining the CALENDAR rather than only erroring on submit is the point:
 * the user sees which dates are available while choosing, instead of being told
 * afterwards that the one they picked was wrong.
 *
 * @param {number}  day        candidate day (start-of-day ms)
 * @param {object}  cfg        the field's `rowHierarchy` config
 * @param {object}  rowsAccess { rows, rowIndex } — every row of the group + this row's index
 */
export function violatesRowHierarchy(day, cfg, { rows, rowIndex } = {}) {
  if (day === null || !cfg || !Array.isArray(rows) || rowIndex == null) return false;
  // 'newestFirst' is the only shape today; naming it keeps an 'oldestFirst'
  // list addable as config rather than as a second code path.
  if ((cfg.mode ?? 'newestFirst') !== 'newestFirst') return false;

  const startKey = cfg.startField ?? 'startDate';
  const endKey = cfg.endField ?? 'endDate';

  // The row ABOVE is more recent, so this row must end before that row began.
  const above = rows[rowIndex - 1];
  if (above) {
    const ceiling = getComparableDateDay(above[startKey]);
    if (ceiling !== null && day >= ceiling) return true;
  }

  // The row BELOW is older, so this row must not start before that row ended.
  const below = rows[rowIndex + 1];
  if (below) {
    const floor = getComparableDateDay(below[endKey]) ?? getComparableDateDay(below[startKey]);
    if (floor !== null && day <= floor) return true;
  }

  return false;
}

/**
 * rowHierarchyMessage — why a date was refused, in terms of the OTHER row.
 * "Overlaps the entry above" is actionable; "invalid date" is not.
 */
export function rowHierarchyMessage(cfg, position = 'above') {
  const messages = cfg?.messages ?? {};
  if (position === 'below') {
    return messages.below
      || 'This starts before the entry below it finished. Entries are listed newest first, so they cannot overlap.';
  }
  return messages.above
    || 'This overlaps the entry above it. Entries are listed newest first, so this one must finish before that one started.';
}

export function buildDisabledDate({ field, readSibling, rows, rowIndex, now = appNow }) {
  const rules = field?.validations ?? field?.validation ?? field?.rules ?? [];
  const predicates = [];

  for (const raw of rules) {
    const rule = typeof raw === 'string' ? { type: raw } : raw;
    const type = rule?.type;

    if (type === 'noPastDate') {
      predicates.push((current) => {
        if (!current) return false;
        return getComparableDateDay(current) < now().startOf('day').valueOf();
      });
    }

    if (type === 'noFutureDate') {
      predicates.push((current) => {
        if (!current) return false;
        return getComparableDateDay(current) > now().startOf('day').valueOf();
      });
    }

    if (type === 'minAge') {
      const years = Number(rule?.value ?? 18);
      predicates.push((current) => {
        if (!current) return false;
        return getComparableDateDay(current) > now().subtract(years, 'year').startOf('day').valueOf();
      });
    }

    if (type === 'dateAfterField' || type === 'dateBeforeField') {
      const compareField = rule?.value ?? rule?.compareField ?? rule?.field;
      if (!compareField) continue;
      predicates.push((current) => {
        if (!current) return false;
        const compareValue = readSibling(compareField);
        if (isEmptyValue(compareValue)) return false;
        return violatesPair(
          type,
          getComparableDateDay(current),
          getComparableDateDay(compareValue),
          rule,
          readSibling,
        );
      });
    }
  }

  // Cross-row constraint. Declared on the FIELD (`rowHierarchy`) but evaluated
  // against the whole group, which is why buildDisabledDate needs `rows` and
  // `rowIndex` — a per-row `readSibling` cannot see the neighbouring rows.
  if (field?.rowHierarchy && Array.isArray(rows) && rowIndex != null) {
    predicates.push((current) => {
      if (!current) return false;
      return violatesRowHierarchy(getComparableDateDay(current), field.rowHierarchy, { rows, rowIndex });
    });
  }

  return predicates.length
    ? (current) => predicates.some((predicate) => predicate(current))
    : undefined;
}

/**
 * defaultPickerValue — which month the calendar opens on, so a Date of Birth
 * field does not open on today and make the user page back 30 years.
 */
export function defaultPickerValue(field, now = appNow) {
  const rules = field?.validations ?? field?.validation ?? field?.rules ?? [];
  for (const raw of rules) {
    const rule = typeof raw === 'string' ? { type: raw } : raw;
    if (rule?.type === 'minAge') return now().subtract(Number(rule?.value ?? 18), 'year');
    if (rule?.type === 'noFutureDate' || rule?.type === 'noPastDate') return now();
  }
  return undefined;
}

/**
 * buildRowHierarchyValidator — the submit-time counterpart of the greyed-out
 * calendar.
 *
 * The picker stops a date being CHOSEN; this stops one that arrived another way
 * — typed, pasted, prefilled from a résumé, or entered before the neighbouring
 * row was filled in. A calendar constraint with no validator behind it is a
 * suggestion.
 */
export function buildRowHierarchyValidator({ field, rows, rowIndex }) {
  const cfg = field?.rowHierarchy;
  return {
    validator: async (_, input) => {
      if (!cfg || isEmptyValue(input) || !Array.isArray(rows) || rowIndex == null) {
        return Promise.resolve();
      }
      const day = getComparableDateDay(input);
      if (day === null) return Promise.resolve();
      if (!violatesRowHierarchy(day, cfg, { rows, rowIndex })) return Promise.resolve();

      // Name WHICH neighbour it clashes with, so the fix is obvious.
      const startKey = cfg.startField ?? 'startDate';
      const above = rows[rowIndex - 1];
      const ceiling = above ? getComparableDateDay(above[startKey]) : null;
      const position = (ceiling !== null && day >= ceiling) ? 'above' : 'below';
      return Promise.reject(new Error(rowHierarchyMessage(cfg, position)));
    },
  };
}
